/*
  Bolinha de vídeo do app Moast (<moast-floating>, shadow root aberto) na
  página do produto:
  - o cliente arrasta a bolinha para qualquer lugar da tela (mouse ou toque);
    arrastar não abre o vídeo; a posição vale para as próximas páginas da sessão;
  - 3 s depois de a bolinha aparecer, sai dela o aviso "VÍDEO DO PRODUTO",
    que sai de trás dela, fica 3 s e volta para trás (uma vez por página).
  Só mexe no elemento que o app cria; o código do app não muda.
*/
(() => {
  const ARRASTO_MIN = 6; // px até virar arrasto (abaixo disso é clique)
  const MARGEM = 8; // px de folga até a borda da tela
  const AVISO_ESPERA = 3000;
  const AVISO_DURACAO = 3000;
  const CHAVE = 'carraro_moast_posicao';

  const ESTILO = `
    .bubble-container.carraro-arrastavel { touch-action: none; cursor: grab; }
    .bubble-container.carraro-arrastando { cursor: grabbing; transition: none !important; }
    .bubble-container.carraro-arrastando .bubble { pointer-events: none; }
    /* A bolinha fica na frente; o aviso nasce atrás dela e desliza para fora. */
    .bubble-container .bubble { position: relative; z-index: 2; }
    .bubble-container .close-button { z-index: 3 !important; }
    .carraro-aviso {
      position: absolute;
      top: 50%;
      z-index: 1;
      display: flex;
      align-items: center;
      height: 36px;
      border-radius: 18px;
      background: #00A4AA;
      color: #fff;
      font: 700 12px/1 'Konstanz', system-ui, sans-serif;
      letter-spacing: .06em;
      white-space: nowrap;
      box-shadow: 0 6px 18px rgba(27, 58, 78, .22);
      pointer-events: none;
      transition: clip-path .55s cubic-bezier(.2, .8, .2, 1), transform .55s cubic-bezier(.2, .8, .2, 1);
    }
    /* Ancorado no centro da bolinha (parte escondida atrás dela); o recorte abre
       de dentro para fora, então o aviso parece sair de trás da bolinha. */
    .carraro-aviso.para-direita {
      left: 50%;
      padding: 0 18px 0 62px;
      clip-path: inset(0 100% 0 0 round 18px);
      transform: translate(-24px, -50%);
    }
    .carraro-aviso.para-esquerda {
      right: 50%;
      padding: 0 62px 0 18px;
      clip-path: inset(0 0 0 100% round 18px);
      transform: translate(24px, -50%);
    }
    .carraro-aviso.visivel { clip-path: inset(0 0 0 0 round 18px); transform: translate(0, -50%); }
    @media (prefers-reduced-motion: reduce) { .carraro-aviso { transition: none; } }
  `;

  function limitar(x, y, largura, altura) {
    return [
      Math.min(Math.max(MARGEM, x), innerWidth - largura - MARGEM),
      Math.min(Math.max(MARGEM, y), innerHeight - altura - MARGEM),
    ];
  }

  function posicionar(caixa, x, y) {
    const r = caixa.getBoundingClientRect();
    const [lx, ly] = limitar(x, y, r.width, r.height);
    caixa.style.setProperty('left', `${lx}px`, 'important');
    caixa.style.setProperty('top', `${ly}px`, 'important');
    caixa.style.setProperty('right', 'auto', 'important');
    caixa.style.setProperty('bottom', 'auto', 'important');
    return [lx, ly];
  }

  function tornarArrastavel(caixa) {
    if (caixa.dataset.carraroArrastavel) return;
    caixa.dataset.carraroArrastavel = '1';
    caixa.classList.add('carraro-arrastavel');

    // Posição escolhida antes, nesta sessão.
    try {
      const salva = JSON.parse(sessionStorage.getItem(CHAVE) || 'null');
      if (salva) requestAnimationFrame(() => posicionar(caixa, salva.x, salva.y));
    } catch (_) {}

    let inicio = null; // { px, py, x, y, id }
    let arrastou = false;
    let quadro = 0;
    let ultimo = null;

    // O app para a propagação dos pointermove depois do pointerdown (tem
    // gestos próprios): durante o arrasto, o movimento é lido no window, em
    // captura, que roda antes dos listeners do app.
    const mover = (e) => {
      if (!inicio || e.pointerId !== inicio.id) return;
      const dx = e.clientX - inicio.px;
      const dy = e.clientY - inicio.py;
      if (!arrastou) {
        if (Math.hypot(dx, dy) < ARRASTO_MIN) return;
        arrastou = true;
        caixa.classList.add('carraro-arrastando');
      }
      e.preventDefault();
      ultimo = [inicio.x + dx, inicio.y + dy];
      if (!quadro) {
        quadro = requestAnimationFrame(() => {
          quadro = 0;
          if (ultimo) posicionar(caixa, ultimo[0], ultimo[1]);
        });
      }
    };

    const soltar = (e) => {
      if (!inicio || e.pointerId !== inicio.id) return;
      inicio = null;
      removeEventListener('pointermove', mover, true);
      removeEventListener('pointerup', soltar, true);
      removeEventListener('pointercancel', soltar, true);
      if (!arrastou) return;
      // O click (se vier) chega nesta mesma tarefa; depois disso, libera.
      setTimeout(() => { arrastou = false; }, 0);
      caixa.classList.remove('carraro-arrastando');
      if (ultimo) {
        const [x, y] = posicionar(caixa, ultimo[0], ultimo[1]);
        try { sessionStorage.setItem(CHAVE, JSON.stringify({ x, y })); } catch (_) {}
      }
    };

    caixa.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || e.target.closest?.('.close-button')) return;
      const r = caixa.getBoundingClientRect();
      inicio = { px: e.clientX, py: e.clientY, x: r.left, y: r.top, id: e.pointerId };
      arrastou = false;
      ultimo = null;
      addEventListener('pointermove', mover, { capture: true, passive: false });
      addEventListener('pointerup', soltar, true);
      addEventListener('pointercancel', soltar, true);
    }, true);

    // O clique que encerra um arrasto não abre o vídeo.
    addEventListener('click', (e) => {
      if (!arrastou) return;
      arrastou = false;
      e.stopImmediatePropagation();
      e.preventDefault();
    }, true);

    addEventListener('resize', () => {
      if (!caixa.style.left) return;
      const r = caixa.getBoundingClientRect();
      posicionar(caixa, r.left, r.top);
    }, { passive: true });
  }

  function mostrarAviso(caixa) {
    if (caixa.dataset.carraroAviso) return;
    caixa.dataset.carraroAviso = '1';
    setTimeout(() => {
      if (!caixa.isConnected || !caixa.classList.contains('is-open')) return;
      const aviso = document.createElement('div');
      const r = caixa.getBoundingClientRect();
      aviso.className = 'carraro-aviso ' + (r.left + r.width / 2 < innerWidth / 2 ? 'para-direita' : 'para-esquerda');
      aviso.setAttribute('aria-hidden', 'true');
      aviso.textContent = 'VÍDEO DO PRODUTO';
      caixa.appendChild(aviso);
      requestAnimationFrame(() => requestAnimationFrame(() => aviso.classList.add('visivel')));
      setTimeout(() => {
        aviso.classList.remove('visivel');
        setTimeout(() => aviso.remove(), 600);
      }, AVISO_DURACAO + 500);
    }, AVISO_ESPERA);
  }

  function preparar(flutuante) {
    const raiz = flutuante.shadowRoot;
    if (!raiz || raiz.querySelector('style.carraro-moast')) return;
    const estilo = document.createElement('style');
    estilo.className = 'carraro-moast';
    estilo.textContent = ESTILO;
    raiz.appendChild(estilo);

    const verificar = () => {
      const caixa = raiz.querySelector('.bubble-container');
      if (!caixa) return;
      tornarArrastavel(caixa);
      if (caixa.classList.contains('is-open')) mostrarAviso(caixa);
    };
    verificar();
    new MutationObserver(verificar).observe(raiz, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  }

  function iniciar() {
    document.querySelectorAll('moast-floating').forEach((f) => {
      if (f.shadowRoot) preparar(f);
    });
  }

  // O app define o elemento depois do carregamento; o shadow root nasce com ele.
  customElements.whenDefined('moast-floating').then(() => {
    iniciar();
    // Garante quando o shadow root é montado um pouco depois da definição.
    let tentativas = 0;
    const repetir = setInterval(() => {
      iniciar();
      if (++tentativas >= 20 || document.querySelector('moast-floating')?.shadowRoot?.querySelector('style.carraro-moast')) clearInterval(repetir);
    }, 500);
  });
})();
